import React from 'react';
import { ChevronLeft, Send, SkipForward } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../shared/utils';

interface NavigationBarProps {
  canSubmit: boolean;
  canSkip?: boolean;
  canGoBack?: boolean;
  isFirstSlide: boolean;
  isLastSlide: boolean;
  isSubmitting: boolean;
  /**
   * Slide-ul curent cere un raspuns de la utilizator (P1-14)? Citit din
   * `plugin.capabilities` de catre apelant (`isScoreable || requiresUpload`),
   * nu presupus aici din tipul slide-ului. Implicit `true`: pastreaza
   * eticheta veche ("Trimite raspuns") daca apelantul nu specifica nimic.
   */
  requiresAnswer?: boolean;
  validationErrors?: string[];
  onSubmit: () => void;
  onSkip?: () => void;
  onPrevious?: () => void;
  className?: string;
}

export const NavigationBar: React.FC<NavigationBarProps> = ({
  canSubmit,
  canSkip = false,
  canGoBack = false,
  isFirstSlide,
  isLastSlide,
  isSubmitting,
  requiresAnswer = true,
  validationErrors = [],
  onSubmit,
  onSkip,
  onPrevious,
  className = '',
}) => {
  // ✅ LAYER 1: Local click state - prevents double-clicks before parent state updates
  const [isClicking, setIsClicking] = React.useState(false);

  const hasErrors = validationErrors.length > 0;

  // Synchronized click handler with immediate local state update
  const handleSubmit = React.useCallback(() => {
    // Guard against rapid clicks or invalid states
    if (isSubmitting || isClicking || !canSubmit) {
      return;
    }

    // Update local state immediately (synchronous)
    setIsClicking(true);

    // Call parent handler
    onSubmit();

    // Auto-reset after 2 seconds (safety timeout)
    // In normal cases, parent isSubmitting will become true immediately
    const timeout = setTimeout(() => setIsClicking(false), 2000);

    return () => clearTimeout(timeout);
  }, [isSubmitting, isClicking, canSubmit, onSubmit]);

  return (
    <div className={cn('px-2 py-1', className)}>
      <div className="max-w-4xl mx-auto">
        {/* Validation errors */}
        {hasErrors && (
          <div className="text-center mb-1">
            <p className="text-sm text-destructive text-extra-bold-black">
              {validationErrors[0]}
            </p>
          </div>
        )}

        {/* Mobil: CTA primar singur pe rând, full-width; secundarele dedesubt ca text links (R8).
            Desktop (≥640px): un singur rând — Înapoi · Trimite · Omite. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-4">
          {/* Submit — acțiunea primară */}
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting || isClicking}
            className={cn(
              'w-full sm:w-auto sm:min-w-[200px] gap-2 touch-target order-1 sm:order-2',
              isLastSlide && 'bg-success hover:bg-success-hover'
            )}
            size="lg"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Se trimite...
              </>
            ) : isLastSlide ? (
              <>
                <Send className="w-5 h-5" />
                Finalizează
              </>
            ) : requiresAnswer ? (
              <>
                <Send className="w-5 h-5" />
                Trimite răspuns
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Mai departe
              </>
            )}
          </Button>

          {/* Secundare: pe mobil un rând sub CTA, pe desktop de o parte și de alta (sm:contents) */}
          <div className="flex items-center justify-between order-2 sm:contents">
            <div className="sm:order-1">
              {canGoBack && !isFirstSlide && onPrevious && (
                <Button
                  onClick={onPrevious}
                  variant="ghost"
                  className="gap-1 px-2 touch-target-link"
                  disabled={isSubmitting}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Înapoi
                </Button>
              )}
            </div>
            <div className="sm:order-3">
              {canSkip && onSkip && !isLastSlide && (
                <Button
                  onClick={onSkip}
                  variant="ghost"
                  className="gap-1 px-2 touch-target-link"
                  disabled={isSubmitting}
                >
                  Omite
                  <SkipForward className="w-5 h-5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Additional validation messages */}
        {hasErrors && validationErrors.length > 1 && (
          <div className="mt-3 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
            <p className="text-sm font-medium text-destructive mb-2">
              Erori de validare:
            </p>
            <ul className="space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index} className="text-sm text-destructive flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>{error}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigationBar;

